import React from 'react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'

import { Button, Modal } from '@repo/ui'

import { accessSettingOption, type AccessSettingKey } from 'constants/accessSettings'
import { SelectionDot } from 'components/display/SelectionDot'

import { ConfirmSettingModal } from '../ConfirmSettingModal'

/** `null` is a real choice here — "inherit from the level above". */
type Choice = boolean | null

interface EditAccessSettingModalProps {
  open: boolean
  onClose: () => void
  setting: AccessSettingKey
  scope: 'product' | 'certificate'
  /** The record's current own override; `null` = currently inheriting. */
  current: Choice
  /** What it resolves to when inheriting, so the inherit row can name it. */
  inheritedValue: boolean
  loading?: boolean
  onSave: (next: Choice) => void
}

/**
 * VRG-591 — pick, then confirm, ONE certificate-access override for one product
 * or one certificate.
 *
 * Structurally a copy of `EditActivationTriggerModal`, deliberately: the two are
 * the same interaction (a tri-state override picker behind a consequence gate) and
 * the alternative was a second, subtly different modal for the same act.
 *
 * TWO SEQUENTIAL MODALS, NEVER TWO AT ONCE. Choosing is a different act from
 * accepting a consequence, so the picker CLOSES when Save is pressed and the
 * confirm opens; cancelling the confirm reopens the picker with the selection
 * intact rather than throwing the choice away. Stacking them would put a scrim
 * over the options the reader is being asked to accept.
 *
 * The gate fires BEFORE anything is applied (§43): nothing is sent until confirm,
 * so cancelling cannot leave a row showing a value that was never stored.
 *
 * ONE FLAG PER MODAL. The two flags resolve independently, so a combined modal
 * would have to state both consequences at once and would let a user accept one
 * while only having read the other.
 *
 * (This note used to justify the split as "one is an access gate, the other is
 * passive telemetry". That is no longer the difference — both are gates on the
 * certificate's details now. The independence of the two resolution chains is what
 * the split actually rests on, and it did not depend on the old asymmetry.)
 */
export const EditAccessSettingModal = ({
  open,
  onClose,
  setting,
  scope,
  current,
  inheritedValue,
  loading,
  onSave,
}: EditAccessSettingModalProps) => {
  const theme = useTheme()
  const option = accessSettingOption(setting)
  const Icon = option.icon

  const [choice, setChoice] = React.useState<Choice>(current)
  const [pending, setPending] = React.useState<Choice | undefined>(undefined)

  /*
    Re-seed on open so the picker never shows the previous record's selection, and
    RESET on close — the half that `EditActivationTriggerModal` records as having
    been missing. `pending` is local state, so deriving `confirming` from it alone
    leaves the confirm mounted when the parent closes the component on a successful
    save, which reads exactly as "Confirm did nothing".
  */
  React.useEffect(() => {
    setPending(undefined)
    if (open) setChoice(current)
  }, [open, current])

  const confirming = open && pending !== undefined
  // §54 — dead until the picker is dirty. An enabled Save on an untouched picker
  // promises an action that would do nothing, and here it would also open a
  // consequence modal for a change that isn't one.
  const dirty = choice !== current

  const inheritedTitle = inheritedValue ? option.onTitle : option.offTitle

  const options: Array<{
    id: Choice
    title: string
    description: string
  }> = [
    {
      id: null,
      title: `Inherit — ${inheritedTitle}`,
      description:
        scope === 'certificate'
          ? 'Follow the product, and then the company default. Changing either will change this certificate.'
          : 'Follow the company default. Changing it will change this product.',
    },
    {
      id: true,
      title: option.onTitle,
      description: option.consequenceOn(scope),
    },
    {
      id: false,
      title: option.offTitle,
      description: option.consequenceOff(scope),
    },
  ]

  return (
    <>
      <Modal
        /*
          The NOUN, matching `EditActivationTriggerModal`'s "Activation for this
          product". `option.label` is the imperative ("Require a location to view")
          and reads as an instruction with " for this certificate" bolted on.
        */
        title={`${option.noun} for this ${scope}`}
        open={open && !confirming}
        onClose={loading ? () => {} : onClose}
        actions={
          <Stack direction="row" spacing={3}>
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={() => setPending(choice)} disabled={loading || !dirty}>
              Save
            </Button>
          </Stack>
        }
      >
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {scope === 'product'
              ? "This applies to every certificate for this product, including ones you have already issued."
              : 'This applies to this one certificate. It overrides whatever the product and the company say.'}
          </Typography>

          {options.map(entry => {
            const selected = entry.id === choice
            return (
              <Box
                key={String(entry.id)}
                role="radio"
                aria-checked={selected}
                onClick={() => setChoice(entry.id)}
                sx={{
                  display: 'flex',
                  gap: 3,
                  p: 5,
                  borderRadius: 3,
                  cursor: 'pointer',
                  // §34 selectable-card: tint = "considering", border = "chosen".
                  // Never a brand border on hover — it would collide with the
                  // selected state and destroy the distinction.
                  border: `1px solid ${
                    selected ? theme.palette.brand[500] : theme.palette.primary[100]
                  }`,
                  backgroundColor: selected
                    ? alpha(theme.palette.brand[500], 0.1)
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: selected
                      ? alpha(theme.palette.brand[500], 0.1)
                      : alpha(theme.palette.brand[500], 0.06),
                  },
                }}
              >
                <Box sx={{ mt: '2px' }}>
                  <SelectionDot selected={selected} />
                </Box>
                {/*
                  ALWAYS an 18px box, even on the inherit row where the glyph is the
                  same one — a conditional element would collapse the column for one
                  row and shift its text left of every other row's. `mt` matches the
                  dot rather than `alignItems: center`, so it sits on the FIRST line
                  of a two-line block instead of drifting as the description wraps.
                */}
                <Box
                  aria-hidden
                  sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    mt: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.text.secondary,
                  }}
                >
                  <Icon size={18} strokeWidth={1} />
                </Box>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.primary">
                    {entry.title}
                  </Typography>
                  <Typography
                    variant="body3"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    {entry.description}
                  </Typography>
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </Modal>

      <ConfirmSettingModal
        open={confirming}
        // Cancelling the consequence returns to the picker with the choice intact.
        onClose={() => setPending(undefined)}
        /*
          DO NOT clear `pending` here — clearing it unmounts the confirm on the same
          tick the request fires, and because the picker's `open` is still true the
          picker reappears underneath, which reads as "the button did nothing". The
          confirm stays mounted in its `loading` state and the PARENT closes the
          whole thing on success. (Recorded on EditActivationTriggerModal, where it
          was reported as an unclickable Confirm.)
        */
        onConfirm={() => onSave(pending as Choice)}
        /*
          `option.subject`, NOT `option.label.toLowerCase()`. The label is an
          imperative ("Require a location to view"), and dropping an imperative into
          the object slot of a question produces "Change require a location to view
          for this product?". The subject form is the noun phrase written for
          exactly this position.
        */
        title={
          scope === 'product'
            ? `Change the ${option.noun.toLowerCase()} for this product?`
            : `Change the ${option.noun.toLowerCase()} for this certificate?`
        }
        consequence={
          pending === null
            ? `This ${scope} will follow ${
                scope === 'certificate' ? 'its product, then ' : ''
              }the company default (${inheritedTitle}). If that default changes later, this will change with it.`
            : pending
              ? option.consequenceOn(scope)
              : option.consequenceOff(scope)
        }
        confirmLabel={
          pending === null
            ? 'Clear override'
            : pending
              ? option.confirmOn
              : option.confirmOff
        }
        loading={loading}
      />
    </>
  )
}
